/**
 * The second line of a connection card (round B U6). SQLite shows its file; a server engine `server:port`, without a
 * port that is empty, "0" (Azure Storage stores port "0" — the card read "account:0") or "<auto>"; an Oracle TNS
 * connection its alias or its descriptor's host / port / service (`describeTarget`, Option C C2 — its server is only a
 * synthesized name, C0; never the raw descriptor, which can carry a wallet password — C2 review C2-03); an Azure Storage
 * connection reads "account · authentication" (or the attached resource's account), so two connections to one account
 * differ. Secrets typed into a server field are masked.
 */
import type { Connection } from '../../../types/electron';
import { safeConnectionTarget, safeServerText } from '../../../types/safeConnectionDisplay';
import { resolveStorageEndpoints } from '../../../core/engines/azureStorageEndpointResolution';

/** The tooltip of every "read-only" marker (tree row, Home card) — the same words for every engine. */
export const READ_ONLY_CONNECTION_TITLE = 'Read-only connection: writes are refused. Edit the connection to change it.';

type TargetInput = Pick<Connection, 'server'> & Partial<Pick<Connection, 'engine' | 'port' | 'filePath' | 'oracleConnectionStringType' | 'oracleTnsAlias' | 'oracleTnsAdmin' | 'oracleTnsDescriptor'>>;
type ServerLineInput = TargetInput & Partial<Pick<Connection, 'authentication' | 'azureStorageEndpoints' | 'azureStorageEmulator'>>;

/**
 * Where a connection dials, as one line (`describeTarget`, shared with the connection-transfer rows): the SQLite file,
 * the Oracle TNS alias or the descriptor's host / port / service (never the descriptor itself — C2 review C2-03), else
 * `server:port` with no empty, zero or automatic port, and any secret typed into the server masked. The connection
 * dialog's success line says "Connected to <this>".
 */
export function connectionTargetLine(connection: TargetInput): string {
  return safeConnectionTarget(connection);
}

export function connectionServerLine(connection: ServerLineInput): string {
  if (connection.engine === 'azurestorage') {
    const account = resolveStorageEndpoints(connection).accountName ?? safeServerText(connection.server);
    const where = connection.azureStorageEmulator ? `${account} (Azurite)` : account;
    return connection.authentication ? `${where} · ${connection.authentication}` : where;
  }
  return connectionTargetLine(connection);
}
